"use client";

import { Battery, ChevronRight, Cpu, ShieldCheck, Smartphone, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CapitalAccess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const chips = [{
    icon: Zap,
    label: "Elite Funding"
  }, {
    icon: Battery,
    label: "Wealth Strategy"
  }, {
    icon: ShieldCheck,
    label: "Verified Results"
  }];
  const callouts = [{
    label: "§ 01",
    top: "10%",
    left: "8%"
  }, {
    label: "CREDIT ARCHITECTURE",
    top: "62%",
    left: "4%"
  }, {
    label: "EST. 2019",
    top: "28%",
    right: "6%"
  }];
  return <section data-section-id="3" id="capital-access" className="bg-background text-foreground relative overflow-hidden min-h-[80vh] flex items-center py-20 lg:py-24">
      {}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[700px] h-[700px] rounded-lg opacity-60 blur-3xl" style={{
      background: "radial-gradient(circle at 30% 30%, hsl(222 47% 11% / 0.55), transparent 55%), radial-gradient(circle at 70% 70%, hsl(162 45% 15% / 0.5), transparent 60%)"
    }} aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-lg opacity-40 blur-3xl" style={{
      background: "radial-gradient(circle, hsl(43 45% 65% / 0.35), transparent 70%)"
    }} aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "linear-gradient(to right, hsl(222 47% 11% / 1) 1px, transparent 1px), linear-gradient(to bottom, hsl(222 47% 11% / 1) 1px, transparent 1px)",
      backgroundSize: "50px 50px"
    }} aria-hidden="true" />

      <div ref={ref} className={`relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-6 items-center">
          {}
          <div className="lg:col-span-6 relative z-20">
            {}
            <div className="inline-flex items-center gap-2 font-mono text-xs sm:text-sm tracking-widest uppercase mb-6 text-primary">
              <ShieldCheck className="w-4 h-4" />
              <span>§ 01 Financial Engineering</span>
            </div>

            {}
            <h1 className="font-default font-medium tracking-tight text-5xl sm:text-6xl lg:text-7xl xl:text-[96px] leading-[0.95] mb-6">
              We Don’t Just Repair Credit. We{" "}
              <span className="bg-clip-text text-transparent" style={{
              backgroundImage: "linear-gradient(135deg, hsl(43 45% 65%), hsl(43 45% 55%), hsl(43 45% 75%))"
            }}>
                Architect
              </span>{" "}
              Solutions.
            </h1>

            {}
            <p className="text-base sm:text-lg text-muted-foreground max-w-[520px] mb-8 font-default">
              Benewealth Financial helps qualified clients strengthen personal credit, build business credit, access real funding solutions, and create strategic pathways toward wealth-building.
            </p>

            {}
            <div className="flex flex-wrap gap-3 mb-10">
              {chips.map((chip, i) => {
              const Icon = chip.icon;
              return <div key={chip.label} data-index={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border/60 bg-secondary/40 backdrop-blur-md text-sm font-medium">
                    <Icon className="w-4 h-4 text-primary" />
                    <span className="font-default">{chip.label}</span>
                  </div>;
            })}
            </div>

            {}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="rounded-md h-12 px-7 text-base bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-transform hover:scale-[1.02]">
                <Link to="/QualificationApplicationPage">
                  Start the Qualification Application
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-md h-12 px-7 text-base border-primary bg-transparent hover:bg-primary/10 text-foreground">
                <Link to="/ServicesPage">Explore Services</Link>
              </Button>
            </div>
            <p className="mt-6 text-xs font-mono text-slate-muted uppercase tracking-widest">
              No SSN required to begin. No hard credit pull for initial review.
            </p>
          </div>

          {}
          <div className="lg:col-span-6 relative h-[400px] sm:h-[480px] lg:h-[560px] lg:-ml-12">
            {}
            <div className="absolute top-1/4 left-1/4 w-72 h-72 rounded-md blur-3xl opacity-60" style={{
            background: "hsl(222 47% 11% / 0.5)"
          }} aria-hidden="true" />
            <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-md blur-3xl opacity-60" style={{
            background: "hsl(162 45% 15% / 0.5)"
          }} aria-hidden="true" />

            {}
            <div className="absolute left-[5%] top-[20%] w-[85%] max-w-[560px]" style={{
            transform: "perspective(1200px) rotateY(-8deg) rotateX(4deg)"
          }}>
              <div className="relative rounded-md overflow-hidden border border-border/60 shadow-2xl">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/wealth_strategy_blueprint.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/wealth_strategy_blueprint.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/wealth_strategy_blueprint.png-wvc-sizes" alt="Elite financial architecture and wealth strategy" loading="lazy" decoding="async" className="w-full h-auto object-cover grayscale contrast-125" />
              </div>
              {}
              <div className="absolute -bottom-6 left-4 right-4 h-12 rounded-md blur-xl opacity-40" style={{
              background: "hsl(43 45% 65% / 0.4)"
            }} aria-hidden="true" />
            </div>

            {}
            <div className="absolute right-[8%] bottom-[8%] w-[28%] max-w-[180px] z-10" style={{
            transform: "rotate(20deg)"
          }}>
              <div className="relative rounded-md overflow-hidden border border-primary/40 shadow-2xl">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/private_office_luxury.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/private_office_luxury.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/private_office_luxury.png-wvc-sizes" alt="Luxury private office setting" loading="lazy" decoding="async" className="w-full h-auto object-cover grayscale" />
              </div>
            </div>

            {}
            {callouts.map((c, i) => <div key={c.label} data-index={i} className="absolute hidden sm:flex items-center gap-2 px-3 py-2 rounded-md border border-primary/30 bg-secondary/80 backdrop-blur-md text-xs font-mono font-medium shadow-lg z-20 text-primary" style={{
            top: c.top,
            left: (c as {
              left?: string;
            }).left,
            right: (c as {
              right?: string;
            }).right
          }}>
                <Cpu className="w-3 h-3 text-primary" />
                <span>{c.label}</span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}